import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CONTROLLER = {
  name: 'Avv. Christian Belliardo — Studio Legale',
  address: 'Salita Fontana del Ferro, 24 — 37129 Verona (Italia)',
  vat: 'P.IVA 03995680232',
  email: 'avvocati@studiobelliardo.it',
  tel: '+39 045.8011276',
};

const PRIVACY_EN = [
  {
    title: '1. Data Controller',
    body: `The data controller is ${CONTROLLER.name}, with registered office at ${CONTROLLER.address}, ${CONTROLLER.vat}. Contact: ${CONTROLLER.email} · Tel. ${CONTROLLER.tel}.`,
  },
  {
    title: '2. Data We Collect',
    body: `Through the "Submit a Claim" form we collect the data you provide voluntarily: company name, contact person, business email address, telephone number, country, approximate claim amount, a description of the matter, and any documents you choose to upload (e.g. contracts, invoices, correspondence).\n\nWhen browsing, our hosting platform processes standard technical connection data (IP address, browser type, access logs). A country-level geolocation check is performed solely to direct Italian visitors to the institutional Italian website; it does not involve profiling.`,
  },
  {
    title: '3. Purposes and Legal Basis',
    body: `Your data is processed exclusively to: (a) carry out a preliminary assessment of your request and, where appropriate, prepare a fee proposal — Art. 6(1)(b) GDPR (pre-contractual measures); (b) comply with legal, tax and professional obligations applicable to the legal profession — Art. 6(1)(c) GDPR; (c) ensure the security of our IT systems — Art. 6(1)(f) GDPR (legitimate interest).\n\nNo marketing, promotional or newsletter activities are carried out on the data submitted through this website.`,
  },
  {
    title: '4. Professional Secrecy',
    body: `All information transmitted through this website is covered by legal professional secrecy pursuant to Art. 622 of the Italian Criminal Code and the Italian Code of Conduct for the legal profession (Codice Deontologico Forense), regardless of whether a mandate is ultimately conferred.`,
  },
  {
    title: '5. Data Retention',
    body: `If no professional mandate follows, the data and documents submitted are deleted within 12 months of the last contact. If a mandate is conferred, the data is retained for the duration of the engagement and thereafter for the periods required by civil and tax law (ordinarily 10 years).`,
  },
  {
    title: '6. Recipients and Transfers',
    body: `Data is hosted on cloud infrastructure and platform providers acting as data processors under Art. 28 GDPR, and transmitted via a transactional email service used solely to notify the firm of new submissions. Data is never sold or disclosed to third parties for their own purposes. Any transfer outside the European Economic Area takes place only under an adequacy decision or Standard Contractual Clauses.`,
  },
  {
    title: '7. Your Rights',
    body: `You may exercise at any time the rights under Articles 15–22 GDPR (access, rectification, erasure, restriction, portability, objection) by writing to ${CONTROLLER.email}. You also have the right to lodge a complaint with the Italian supervisory authority, the Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).`,
  },
  {
    title: '8. Automated Decision-Making',
    body: `No automated decision-making, including profiling, is carried out on the data submitted. Every request is reviewed personally by a lawyer.`,
  },
  {
    title: '9. Security',
    body: `Data is transmitted over encrypted connections (TLS) and stored with access controls and audit logging. Access is limited to the firm's authorised personnel.`,
  },
  {
    title: '10. Updates',
    body: `This notice may be updated to reflect regulatory or operational changes. Last updated: July 2026.`,
  },
];

const PRIVACY_IT = [
  {
    title: '1. Titolare del trattamento',
    body: `Titolare del trattamento è ${CONTROLLER.name}, con sede in ${CONTROLLER.address}, ${CONTROLLER.vat}. Contatti: ${CONTROLLER.email} · Tel. ${CONTROLLER.tel}.`,
  },
  {
    title: '2. Dati raccolti',
    body: `Attraverso il modulo di contatto sono raccolti i dati forniti volontariamente dall’utente: denominazione dell’impresa, persona di contatto, indirizzo email, recapito telefonico, Paese, importo approssimativo del credito, descrizione della pratica ed eventuali documenti caricati volontariamente (ad es. contratti, fatture, corrispondenza). La navigazione comporta il trattamento di dati tecnici di connessione (indirizzo IP, tipo di browser, log di accesso) da parte della piattaforma di hosting.\n\nLa rilevazione del Paese di provenienza della connessione ha la sola finalità di indirizzare i visitatori italiani verso il presente sito istituzionale e non comporta alcuna profilazione.`,
  },
  {
    title: '3. Finalità e base giuridica',
    body: `I dati sono trattati esclusivamente per: (a) l'esame preliminare della richiesta e l'eventuale formulazione di un preventivo — art. 6, par. 1, lett. b) GDPR (misure precontrattuali); (b) l'adempimento degli obblighi di legge, fiscali e deontologici propri della professione forense — art. 6, par. 1, lett. c) GDPR; (c) la sicurezza dei sistemi informatici — art. 6, par. 1, lett. f) GDPR (legittimo interesse).\n\nNon sono svolte attività di marketing, promozione o invio di newsletter sui dati conferiti tramite questo sito.`,
  },
  {
    title: '4. Segreto professionale',
    body: `Tutte le informazioni trasmesse attraverso questo sito sono coperte dal segreto professionale ai sensi dell'art. 622 c.p. e del Codice Deontologico Forense, indipendentemente dal conferimento dell'incarico.`,
  },
  {
    title: '5. Conservazione',
    body: `In assenza di conferimento dell'incarico, i dati e i documenti trasmessi sono cancellati entro 12 mesi dall'ultimo contatto. In caso di incarico, i dati sono conservati per la durata del rapporto e successivamente per i termini previsti dalla normativa civile e fiscale (ordinariamente 10 anni).`,
  },
  {
    title: '6. Destinatari e trasferimenti',
    body: `I dati risiedono su infrastrutture cloud di fornitori nominati responsabili del trattamento ai sensi dell'art. 28 GDPR; le comunicazioni email avvengono tramite un servizio di posta transazionale utilizzato esclusivamente per notificare allo studio le nuove richieste. I dati non sono mai ceduti a terzi per finalità proprie. Eventuali trasferimenti al di fuori dello Spazio Economico Europeo avvengono solo in presenza di decisione di adeguatezza o clausole contrattuali tipo.`,
  },
  {
    title: '7. Diritti dell’interessato',
    body: `È possibile esercitare in qualsiasi momento i diritti di cui agli artt. 15–22 GDPR (accesso, rettifica, cancellazione, limitazione, portabilità, opposizione) scrivendo a ${CONTROLLER.email}. Resta fermo il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).`,
  },
  {
    title: '8. Processi decisionali automatizzati',
    body: `Non è svolto alcun processo decisionale automatizzato, né attività di profilazione. Ogni richiesta è esaminata personalmente da un avvocato.`,
  },
  {
    title: '9. Sicurezza',
    body: `I dati sono trasmessi su connessioni cifrate (TLS) e conservati con controlli di accesso e registrazione delle operazioni. L'accesso è limitato al personale autorizzato dello studio.`,
  },
  {
    title: '10. Aggiornamenti',
    body: `La presente informativa può essere aggiornata per adeguamenti normativi o operativi. Ultimo aggiornamento: luglio 2026.`,
  },
];

const COOKIE_EN = [
  {
    title: '1. What Are Cookies',
    body: `Cookies are small text files that websites store on your device. This website uses exclusively technical cookies and storage necessary for its operation. No profiling, advertising or analytics cookies are used.`,
  },
  {
    title: '2. Technical Cookies and Storage',
    body: `__emg_vid and __emg_sid — technical cookies set by the hosting platform, required for the delivery and security of the website (session and visitor identification at infrastructure level).\n\nBrowser storage (sessionStorage) — used to remember, for the duration of the browser session, the country-routing check that directs Italian visitors to the institutional Italian website, and any voluntary override. This information never leaves your browser.`,
  },
  {
    title: '3. Third-Party Content',
    body: `The Italian institutional pages of this website offer an optional Google Maps map showing the firm's office. The map is loaded only if you explicitly request it by clicking "Load map"; at that point Google LLC may set its own cookies (e.g. NID) and process usage data according to its own privacy policy (policies.google.com/privacy). No other third-party services set cookies through this website.`,
  },
  {
    title: '4. Consent',
    body: `Since this website directly sets only technical cookies and the map is loaded exclusively on your explicit request, no prior consent banner is required under Art. 122 of the Italian Privacy Code and the guidelines of the Garante.`,
  },
  {
    title: '5. Managing Cookies',
    body: `You can delete or block cookies at any time through your browser settings. Blocking technical cookies may affect the functioning of the website.`,
  },
];

const COOKIE_IT = [
  {
    title: '1. Cosa sono i cookie',
    body: `I cookie sono piccoli file di testo che i siti web memorizzano sul dispositivo dell'utente. Questo sito utilizza esclusivamente cookie tecnici e strumenti di memorizzazione necessari al suo funzionamento. Non sono utilizzati cookie di profilazione, pubblicitari o di analisi statistica.`,
  },
  {
    title: '2. Cookie tecnici e memorizzazione locale',
    body: `__emg_vid e __emg_sid — cookie tecnici installati dalla piattaforma di hosting, necessari all'erogazione e alla sicurezza del sito (identificazione della sessione e del visitatore a livello infrastrutturale).\n\nMemorizzazione del browser (sessionStorage) — utilizzata per ricordare, per la sola durata della sessione di navigazione, l'esito della verifica geografica che indirizza i visitatori italiani verso il presente sito istituzionale, ed eventuali scelte volontarie dell'utente. Tale informazione non lascia mai il browser dell'utente.`,
  },
  {
    title: '3. Contenuti di terze parti',
    body: `Le pagine del presente sito offrono una mappa Google Maps opzionale indicante la sede dello studio. La mappa viene caricata solo previa Sua esplicita richiesta, cliccando su "Carica la mappa"; in quel momento Google LLC può installare propri cookie (ad es. NID) e trattare dati di utilizzo secondo la propria informativa (policies.google.com/privacy). Nessun altro servizio di terze parti installa cookie attraverso questo sito.`,
  },
  {
    title: '4. Consenso',
    body: `Poiché il sito installa direttamente solo cookie tecnici e la mappa viene caricata esclusivamente su Sua esplicita richiesta, non è richiesto il banner del consenso preventivo ai sensi dell’art. 122 del Codice Privacy e delle linee guida del Garante.`,
  },
  {
    title: '5. Gestione dei cookie',
    body: `È possibile cancellare o bloccare i cookie in qualsiasi momento tramite le impostazioni del browser. Il blocco dei cookie tecnici può compromettere il funzionamento del sito.`,
  },
];

function LegalLayout({ lang, title, subtitle, sections, testid }) {
  const isIt = lang === 'it';
  const home = isIt ? '/it' : '/';
  const other = isIt
    ? { to: title === 'Privacy Policy' ? '/it/cookie-policy' : '/it/privacy', label: title === 'Privacy Policy' ? 'Cookie Policy' : 'Privacy Policy' }
    : { to: title === 'Privacy Policy' ? '/cookie-policy' : '/privacy', label: title === 'Privacy Policy' ? 'Cookie Policy' : 'Privacy Policy' };

  return (
    <div className="relative bg-ink-950 text-white min-h-screen" data-testid={testid}>
      <Helmet>
        <title>{`${title} — Studio Legale Belliardo`}</title>
        <meta name="description" content={isIt
          ? 'Informativa privacy e cookie — Studio Legale Avv. Christian Belliardo, Verona.'
          : 'Privacy and cookie information — Studio Legale Belliardo, Verona.'} />
      </Helmet>
      <header className="border-b border-white/8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to={home} className="font-serif text-xl md:text-2xl text-white leading-none" data-testid={`${testid}-home-link`}>
            Belliardo<span className="font-mono text-white/40 text-[10px] ml-3 uppercase tracking-[0.25em]">
              {isIt ? 'Studio Legale · Verona' : 'International practice · Verona'}
            </span>
          </Link>
          <Link to={other.to} className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white" data-testid={`${testid}-other-link`}>
            {other.label}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 md:px-10 py-20 md:py-28">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">
          — {isIt ? 'Informativa legale' : 'Legal notice'}
        </p>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-white font-light mb-4">
          {title}
        </h1>
        <p className="text-white/50 text-sm mb-16">{subtitle}</p>

        <div className="space-y-12">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-4">{s.title}</h2>
              <p className="text-white/75 text-base leading-relaxed whitespace-pre-line">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
          {CONTROLLER.name} · {CONTROLLER.address} · {CONTROLLER.vat}
        </div>
      </main>
    </div>
  );
}

export function PrivacyPolicy({ lang = 'en' }) {
  const isIt = lang === 'it';
  return (
    <LegalLayout
      lang={lang}
      testid={isIt ? 'it-privacy-page' : 'privacy-page'}
      title="Privacy Policy"
      subtitle={isIt
        ? 'Informativa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR)'
        : 'Information notice pursuant to Articles 13 and 14 of Regulation (EU) 2016/679 (GDPR)'}
      sections={isIt ? PRIVACY_IT : PRIVACY_EN}
    />
  );
}

export function CookiePolicy({ lang = 'en' }) {
  const isIt = lang === 'it';
  return (
    <LegalLayout
      lang={lang}
      testid={isIt ? 'it-cookie-page' : 'cookie-page'}
      title="Cookie Policy"
      subtitle={isIt
        ? 'Informativa sui cookie ai sensi dell\u2019art. 122 del D.Lgs. 196/2003 e delle linee guida del Garante'
        : 'Cookie notice pursuant to Art. 122 of the Italian Privacy Code and the Garante guidelines'}
      sections={isIt ? COOKIE_IT : COOKIE_EN}
    />
  );
}
